import { type ReactNode, useCallback, useEffect, useState } from 'react';
import type { Checklist, Configuracion, FacturacionApi } from '../api/types';
import { AfiliacionSeccion } from '../components/AfiliacionSeccion';
import { DocumentosSeccion } from '../components/DocumentosSeccion';
import { ProductosSeccion } from '../components/ProductosSeccion';
import { EmisorSeccion } from '../components/EmisorSeccion';
import { SucursalesSeccion } from '../components/SucursalesSeccion';
import { Alerta, Boton, ErrorApi, Pill } from '../components/ui';

type Tab = 'emisor' | 'sucursales' | 'afiliacion' | 'productos' | 'documentos';

const PASOS: { clave: keyof Omit<Checklist, 'listoParaEmitir'>; texto: string; tab: Tab }[] = [
  { clave: 'datosEmisor', texto: 'Datos del emisor', tab: 'emisor' },
  { clave: 'companiaCreadaEnAlanube', texto: 'Registro del comercio', tab: 'emisor' },
  { clave: 'certificadosCargados', texto: 'Certificados', tab: 'emisor' },
  { clave: 'sucursalPrincipal', texto: 'Sucursal principal', tab: 'sucursales' },
  { clave: 'afiliacionAprobada', texto: 'Afiliación DGI', tab: 'afiliacion' },
  { clave: 'qrRegistrado', texto: 'Código QR', tab: 'afiliacion' },
];

export function ConfiguracionFacturacionPanama({ api, idEmpresa }: { api: FacturacionApi; idEmpresa: number }) {
  const [config, setConfig] = useState<Configuracion | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tab, setTab] = useState<Tab>('emisor');
  const [sincronizando, setSincronizando] = useState(false);
  const aplicar = useCallback((c: Configuracion) => setConfig(c), []);

  useEffect(() => {
    let vivo = true;
    setError(null);
    api
      .obtenerConfiguracion(idEmpresa)
      .then((c) => {
        if (!vivo) return;
        aplicar(c);
        // abre en el primer paso pendiente
        const pendiente = PASOS.find((p) => !c.checklist[p.clave]);
        setTab(pendiente ? pendiente.tab : 'documentos');
      })
      .catch((e) => vivo && setError(e));
    return () => {
      vivo = false;
    };
  }, [api, idEmpresa, aplicar]);

  async function sincronizar() {
    setSincronizando(true);
    setError(null);
    try {
      aplicar(await api.sincronizar(idEmpresa));
    } catch (e) {
      setError(e);
    } finally {
      setSincronizando(false);
    }
  }

  if (!config) {
    return (
      <main className="pagina">
        {error ? <ErrorApi error={error} /> : <div className="cargando" aria-busy>Cargando configuración…</div>}
      </main>
    );
  }

  if (!config.habilitado) {
    return (
      <main className="pagina">
        <Encabezado config={config} />
        <Alerta tipo="aviso" titulo="Facturación electrónica no disponible">
          {config.motivoNoHabilitado}
        </Alerta>
      </main>
    );
  }

  const ck = config.checklist;
  const e = config.emisor;

  return (
    <main className="pagina">
      <Encabezado config={config}>
        {ck.listoParaEmitir ? <Pill tono="ok">Listo para emitir</Pill> : <Pill tono="pendiente">Configuración incompleta</Pill>}
        {e?.alanubeIdCompany && (
          <Boton tipo="secundario" onClick={sincronizar} cargando={sincronizando}>
            Actualizar estado
          </Boton>
        )}
      </Encabezado>

      <nav className="progreso" aria-label="Progreso de configuración">
        {PASOS.map((p, i) => (
          <button
            key={p.clave}
            type="button"
            className={'progreso__paso' + (ck[p.clave] ? ' hecho' : '') + (tab === p.tab ? ' en-tab' : '')}
            onClick={() => setTab(p.tab)}
          >
            <span className="progreso__num" aria-hidden>{ck[p.clave] ? '✓' : i + 1}</span>
            <span>{p.texto}</span>
          </button>
        ))}
      </nav>

      <ErrorApi error={error} />
      {e?.ultimoError && (
        <Alerta tipo="aviso" titulo="Último error reportado por el PAC">
          {e.ultimoError}
        </Alerta>
      )}
      {e?.ultimaSincronizacion && (
        <p className="texto-sec sync">Estado actualizado: {new Date(e.ultimaSincronizacion).toLocaleString('es-PA')}</p>
      )}

      <div className="tabs" role="tablist">
        {(
          [
            ['emisor', 'Emisor y certificados'],
            ['sucursales', `Sucursales (${config.sucursales.length})`],
            ['afiliacion', 'Afiliación DGI y QR'],
            ['productos', 'Productos'],
            ['documentos', 'Documentos'],
          ] as const
        ).map(([t, texto]) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'activo' : ''} onClick={() => setTab(t)}>
            {texto}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {tab === 'emisor' && <EmisorSeccion api={api} config={config} onGuardado={aplicar} />}
        {tab === 'sucursales' && <SucursalesSeccion api={api} config={config} onGuardado={aplicar} />}
        {tab === 'afiliacion' && <AfiliacionSeccion api={api} config={config} onGuardado={aplicar} />}
        {tab === 'productos' && <ProductosSeccion api={api} idEmpresa={config.idEmpresa} />}
        {tab === 'documentos' && <DocumentosSeccion api={api} idEmpresa={config.idEmpresa} />}
      </div>
    </main>
  );
}

function Encabezado({ config, children }: { config: Configuracion; children?: ReactNode }) {
  return (
    <header className="pagina__cab">
      <div>
        <p className="eyebrow">Facturación electrónica · Panamá</p>
        <h1>{config.nombreEmpresa}</h1>
      </div>
      <div className="pagina__cab-acciones">{children}</div>
    </header>
  );
}
